/*eslint-disable*/
import cloneDeep from 'lodash/cloneDeep';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

// @mui/material components
import { makeStyles, withStyles } from '@mui/styles';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';


// material ui icons
import Assignment from '@mui/icons-material/Assignment';
import Permission from '@mui/icons-material/LockOpen';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

// core components
import CustomInput from 'components/CustomInput/CustomInput.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import ReactTable from 'components/ReactTable/ReactTable.js';

import {
  primaryColor,
  secondaryColor,
  whiteColor,
} from '@themes/defaultThemeProps';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardIcon from 'components/Card/CardIcon.js';
import Button from 'components/CustomButtons/Button.js';

// style for this view
import styles from 'assets/jss/material-dashboard-pro-react/views/roleEditStyle.js';

// Redux
import Icon from '@mui/material/Icon';
import {
  useCreateRoleMutation,
  useGetPermissionsQuery,
  useGetRolesQuery,
  useUpdateRoleMutation,
} from 'api/role.api';
import { useGetParameterListsQuery } from 'api/parameter.api';
import { selectViewIsDirty, setViewIsDirty } from 'features/ux/uxSlice';
const useStyles = makeStyles(styles);
let pendingRedirectUrl = null;

const isParameterExpired = (parameterSets, currentValue, now) => {
  const selectedSet = parameterSets.find((set) => set.name === currentValue);
  const endDate = selectedSet?.end_date ? new Date(selectedSet.end_date) : null;
  return endDate && endDate < now;
};

const getEmptyRole = (permissions) => {
  const defaultPermissions = permissions.map((permission) => {
    return { name: permission.name, value: permission.default };
  });
  return {
    name: '',
    description: '',
    permissions: defaultPermissions,
    parameters: 'None'
  };
};

export default function Role(props) {
  const classes = useStyles();

  const dispatch = useDispatch();
  const location = useLocation();
  const viewIsDirty = useSelector(selectViewIsDirty);
  const { data: permissions } = useGetPermissionsQuery();
  const { data: roles } = useGetRolesQuery();
  const { data: parameterSets = [] } = useGetParameterListsQuery();
  const [createRole] = useCreateRoleMutation();
  const [updateRole] = useUpdateRoleMutation();

  const newRole = location.state.new;
  const role = newRole ? getEmptyRole(permissions) : location.state.role;
  const originalRole = role.name;

  const [name, setName] = useState(role.name);
  const [nameState, setNameState] = useState('');
  const [nameError, setNameError] = useState('');
  const [description, setDescription] = useState(role.description);
  const [descriptionState, setDescriptionState] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [parameterSet, setParameterSet] = useState(role.parameters ?? 'None');
  const [parameterSetError, setParameterSetError] = useState('');

  const now = new Date();
  const next24Hours = new Date(now.getTime() + 24 * 60 * 60 * 1000);

  const [errorPermissions, setErrorPermissions] = useState('');

  // function that verifies if a string has a given length or not
  const verifyLength = (value, original, length, required = true) => {
    if (value && value.length >= length) {
      if (value !== original) dispatch(setViewIsDirty(true));
      return true;
    } else if (!required && (!value || value.length === 0)) {
      if (value !== original) dispatch(setViewIsDirty(true));
      return true;
    }
    return false;
  };

  const verifyRoleName = (str) => {
    // verify length
    if (verifyLength(str, name, 3)) {
      // check for duplicate
      const role = roles.find((itm) => itm.name === str);
      if (role && str !== originalRole) {
        setNameError(
          'Duplicate Role Name already exists. Enter a different role name.'
        );
        return false;
      } else {
        // check for spaces
        if (/\s/.test(str)) {
          setNameError('Spaces are not allowed in role name.');
          return false;
        }
        setNameError('');
        return true;
      }
    } else {
      setNameError(
        'Field must contain at least 3 alphanumeric or special characters.'
      );
      return false;
    }
  };

  const changeClick = async () => {
    let error = false;
    const updatedRole = cloneDeep(role);

    if (name === '' && nameState === '') {
      error = true;
      setNameState('error');
    } else if (nameState === 'error') error = true;
    if (description === '' && descriptionState === '') {
      error = true;
      setDescriptionState('error');
    } else if (descriptionState === 'error') error = true;

    if (error === false) {
      updatedRole.name = name;
      updatedRole.description = description;
      updatedRole.parameters = parameterSet;
      updatedRole.permissions.map((permission, index) => {
        permission.value = checked[index];
        return permission;
      });
      if (newRole) {
        await createRole(updatedRole).unwrap();
      } else {
        await updateRole(updatedRole).unwrap();
      }

      pendingRedirectUrl = '/admin/roles';
      dispatch(setViewIsDirty(false));
    }
  };

const getInitialPermissions = () => {
    return role.permissions.map((permission) =>  ['True', true, 'true'].includes(permission.value));
  };

  const [checked, _setChecked] = useState(getInitialPermissions);
  const checkedRef = useRef(checked);
  // use proxy setter to keep ref synchronized with state
  const setChecked = (c) => {
    _setChecked(c);
    checkedRef.current = c;
    dispatch(setViewIsDirty(true));
  };

  const handleToggle = (index) => {
    const currentValue = checkedRef.current[index];
    const newChecked = [...checkedRef.current];
    newChecked[index] = !currentValue;
    setChecked(newChecked);
    const newData = [...dataRef.current];
    const newRow = { ...newData[index] };
    newRow.permission = permissionSwitch(newChecked[index], index);
    newData[index] = newRow;
    setData(newData);
  };

  const AntSwitch = withStyles(() => ({
    root: {
      width: 28,
      height: 16,
      padding: 0,
      display: 'flex',
    },
    switchBase: {
      padding: 2,
      color: secondaryColor[1],
      '&$checked': {
        transform: 'translateX(12px)',
        color: whiteColor,
        '& + $track': {
          opacity: 1,
          backgroundColor: primaryColor[0],
          borderColor: primaryColor[0],
        },
      },
    },
    thumb: {
      width: 12,
      height: 12,
      boxShadow: 'none',
    },
    track: {
      border: `1px solid ${primaryColor[0]}`,
      borderRadius: 16 / 2,
      opacity: 1,
      backgroundColor: whiteColor,
    },
    checked: {},
  }))(Switch);

  const permissionSwitch = (value, index) => {
    return (
      <Typography component='div' className={classes.formControl}>
        <Grid component='label' container alignItems='center' spacing={2}>
          <Grid item>False</Grid>
          <Grid item>
            <AntSwitch
              id='booleanValue'
              label='booleanValue'
              checked={value === 'true' || value === true}
              onChange={(e) => handleToggle(index)}
            />
          </Grid>
          <Grid item>True</Grid>
        </Grid>
      </Typography>
    );
  };

  const initialData = () => {
    return role.permissions.map((entry, key) => {
      var permissionData = permissions.find((pd) => pd.name === entry.name);
      const parameterRow = {
        id: key,
        application: permissionData.application,
        category: permissionData.category,
        function: {
          function: permissionData.name,
          description: permissionData.description,
        },
        permission: permissionSwitch(checkedRef.current[key], key),
      };
      return parameterRow;
    });
  };

  const [data, _setData] = useState(initialData);
  const dataRef = useRef(data);

  const setData = (d) => {
    dataRef.current = d;
    _setData(d);
  };

  const formatFunctionCell = ({ value }) => {
    return (
      <div>
        <span className={classes.functionName}>{value.function}</span>
        <span className={classes.functionDescription}>{value.description}</span>
      </div>
    );
  };

  const parameterChangeHandler = (event) => {
    if (event.target.value !== parameterSet) {
      const isExpired = isParameterExpired(parameterSets, event.target.value, now);
      if (isExpired) {
        setParameterSetError('The selected parameter set has expired. Please select a valid set.');
      } else {
        setParameterSetError('');
      }
      setParameterSet(event.target.value);
      dispatch(setViewIsDirty(true));
    }
  };

  const parameterSelection = () => {
    const seen = {};
    const parameterList = parameterSets
      .filter((set) => (Object.hasOwn(seen, set.name) ? false : (seen[set.name] = true)))
      .map((set) => {
        const endDate = set.end_date ? new Date(set.end_date) : null;
        const startDate = set.start_date ? new Date(set.start_date) : null;
        const isExpired = endDate && endDate < now;
        const willExpireSoon = endDate && endDate <= next24Hours && endDate > now;

        let label = null;
        if (isExpired) {
          label = <font className={classes.expirationExpired}>(Expired)</font>;
        } else if (willExpireSoon) {
          label = <font className={classes.expirationSoon}>(Will expire soon)</font>;
        } else if (startDate && startDate > now) {
          label = <font className={classes.expirationFuture}>(Available in future)</font>;
        }

        return (
          <MenuItem key={set.name} value={set.name}>
            {set.name} {label}
          </MenuItem>
        );
      });

    return (
      <>
        <Select
          variant='standard'
          className={classes.select}
          labelId='parameter-select-label'
          id='parameter-select'
          value={parameterSet}
          onChange={parameterChangeHandler}
        >
          <MenuItem key='none' value='None'>
            None
          </MenuItem>
          {parameterList}
        </Select>
        {parameterSetError && (
          <FormHelperText style={{ color: 'red' }}>
            {parameterSetError}
          </FormHelperText>
        )}
      </>
    );
  };




  useEffect(() => {
    dispatch(setViewIsDirty(false));
  }, []);

  useEffect(() => {
    if (pendingRedirectUrl) {
      props.history.push(pendingRedirectUrl);
      pendingRedirectUrl = null;
    }
  }, [viewIsDirty]);

  useEffect(() => {
    const hasEnabledPermission = checkedRef.current.some(
      (value) => value === true || value === 'true'
    );

    if (!hasEnabledPermission) {
      setErrorPermissions(
        'You must enable at least one permission to save this role'
      );
    } else {
      setErrorPermissions('');
    }
  }, [checkedRef.current, data]);

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color='rose' icon>
            <CardIcon color='rose'>
              <Assignment />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Role Information</h4>
          </CardHeader>
          <CardBody>
            <GridContainer>
              <GridItem xs={12}>
                <CustomInput
                  helperText={
                    nameState === 'error'
                      ? nameError ?? 'An error has occurred'
                      : false
                  }
                  success={nameState === 'success'}
                  error={nameState === 'error'}
                  labelText='Role Name'
                  id='name'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    defaultValue: name,
                    onChange: (event) => {
                      if (verifyRoleName(event.target.value)) {
                        setNameState('success');
                      } else {
                        setNameState('error');
                      }
                      setName(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />
                <CustomInput
                  helperText={
                    descriptionState === 'error'
                      ? descriptionError ?? 'An error has occurred'
                      : false
                  }
                  success={descriptionState === 'success'}
                  error={descriptionState === 'error'}
                  labelText='Role Description'
                  id='description'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    defaultValue: description,
                    onChange: (event) => {
                      if (verifyLength(event.target.value, description, 3)) {
                        setDescriptionState('success');
                        setDescriptionError('');
                      } else {
                        setDescriptionState('error');
                        setDescriptionError(
                          'Field must contain at least 3 alphanumeric or special characters'
                        );
                      }
                      setDescription(event.target.value);
                    },
                    autoComplete: 'off',
                  }}
                />

                <FormControl
                  variant='standard'
                  className={classes.formControl}
                  fullWidth={true}
                >
                  <InputLabel
                    id='parameter-select-label'
                    className={classes.inputLabel}
                  >
                    Parameter Set
                  </InputLabel>
                  {parameterSelection()}
                </FormControl>

                <Button
                  color='rose'
                  onClick={changeClick}
                  className={classes.registerButton}
                  disabled={
                    !viewIsDirty ||
                    nameState === 'error' ||
                    parameterSetError !== '' ||
                    !checkedRef.current.some(
                      (value) => value === true || value === 'true'
                    )
                  }
                >
                  Save Changes
                </Button>
                <Button
                  color='secondary'
                  onClick={() => props.history.push('/admin/roles')}
                  className={classes.registerButton}
                >
                  Cancel
                </Button>
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>
      </GridItem>
      <GridItem xs={12}>
        <Card>
          <CardHeader color='rose' icon>
            <CardIcon color='rose'>
              <Permission />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Permissions</h4>
            {errorPermissions && (
              <div className={classes.errPermissionMsg}>
                <Icon>info</Icon> {errorPermissions}
              </div>
            )}
          </CardHeader>
          <CardBody>
            <ReactTable
              columns={[
                {
                  Header: 'Category',
                  accessor: 'category',
                  disableSortBy: true,
                  width: 100,
                },
                {
                  Header: 'Function',
                  accessor: 'function',
                  Cell: formatFunctionCell,
                  filter: 'function',
                  disableSortBy: true,
                  width: 200,
                },
                {
                  Header: 'Permission',
                  accessor: 'permission',
                  disableFilters: true,
                  disableSortBy: true,
                  width: 100,
                  style: {
                    marginBottom: 20,
                  },
                },
              ]}
              data={dataRef.current}
              options={{
                pageSize: dataRef.current.length,
              }}
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
